import React, { Fragment, useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';

// API & Models
import { getDaisyDepositIntervals } from '../../api/getDaisyDepositIntervals';
import { getEnvelopesWithGroup } from '../../api/getEnvelopesWithGroup';
import { getDaisyEurRates, DaisyEurRates } from '../../api/getDaisyEurRates';
import { responseHero } from '../../utils/axiosInterceptor';

// API - Manual Payouts
import { getDaisyPayoutRegister } from '../../api/getDaisyPayoutRegister';
import { removeDaisyPayoutRegister } from '../../api/removeDaisyPayoutRegister';
// Models
import { Entity } from '../../models/types/DaisyDepositIntervals';
import { Envelope } from '../../models/envelope.model';
import { User } from '../../models/user.model';
import { DaisyPayoutRegister } from '../../models/daisypayoutregister.model'; // Imported shared model

// Components
import DaisyPayoutModal from '../../components/daisy/modals/AddNewDaisyPayoutModal';

// Utils
import { currencyFormatter } from '../../utils/currencyFormatter';

// --- Constants ---
const fixedStartDate = '2021-06-22';
const CACHE_KEYS = {
  INTERVALS: 'daisy_cache_intervals',
  ENVELOPES: 'daisy_cache_envelopes',
  RATES: 'daisy_cache_rates'
};

export const options = [
  {
    id: 1,
    name: '1. csoport',
    caption: '3 év, vagy annál több',
    startDate: fixedStartDate,
    endDate: '2022-05-31',
    paymentDeadline: '2025-09-30',
  },
  {
    id: 2,
    name: '2. csoport',
    caption: '2-3 év között',
    startDate: '2022-06-01',
    endDate: '2023-05-31',
    paymentDeadline: '2025-12-31',
  },
  {
    id: 3,
    name: '3. csoport',
    caption: '1-2 év között',
    startDate: '2023-06-01',
    endDate: '2024-06-01',
    paymentDeadline: '2026-03-30',
  },
];

// --- Types ---

export type ContractDetail = {
  key: string; 
  user: User;
  userId: number;
  groupId: number;
  year_category_id: number;
  year_category: string;
  until_date: string;
  invested_date: string;
  deposit: number;
  withdraw: number;
  diff: number;
  remainingAmount: number;
  currency: string;
}

type UserGroupedData = {
  user: User;
  details: ContractDetail[];
  rawEntities: Entity[];
  totalDeposit: number;
  totalWithdraw: number;
  totalDiff: number;
}

export default function DaisyDepositIntervalPage() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Entity[]>([]);
  const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
  const [eurRates, setEurRates] = useState<DaisyEurRates[]>([]);
  const [nameQuery, setNameQuery] = useState("");
  
  // Logic State
  const [payoutRelevanceMap, setPayoutRelevanceMap] = useState<Record<string, boolean>>({});
  const [loadingPayouts, setLoadingPayouts] = useState<Record<number, boolean>>({});
  const [contractDetailsMap, setContractDetailsMap] = useState<Map<string, ContractDetail>>(new Map());

  // Manual Payouts State
  const [manualPayouts, setManualPayouts] = useState<Record<number, DaisyPayoutRegister[]>>({});
  const [loadingManualPayouts, setLoadingManualPayouts] = useState<Record<number, boolean>>({});

  // Modal State
  const [modalState, setModalState] = useState<{ open: boolean; userId: number | null }>({
    open: false,
    userId: null
  });

  // UI State
  const [expandedUserIds, setExpandedUserIds] = useState<Set<number>>(new Set());
  const [debugMode, setDebugMode] = useState<string | null>(null);

  // --- Helpers ---

  const normalize = (s?: string) =>
    (s ?? "").toLocaleLowerCase("hu").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

  const getYearCategory = (dateString: string | Date) => {
    const targetDate = new Date(new Date(dateString).toISOString().split('T')[0]);
    for (const opt of options) {
      const startDate = new Date(opt.startDate);
      const endDate = new Date(opt.endDate);
      if (targetDate >= startDate && targetDate <= endDate) {
        return { id: opt.id, name: opt.name, paymentDeadline: opt.paymentDeadline };
      }
    }
    return { id: -1, name: "Nincs kategória", paymentDeadline: "" };
  };

  const getRadioValue = (value?: string) => {
    switch (value) {
      case "Radio1": return "1. opció";
      case "Radio2": return "2. opció";
      case "Radio3": return "3. opció";
      default: return "Ismeretlen";    
    }
  }

  // --- Matching Logic Helper ---
  const findMatchingContract = useCallback((
      detail: ContractDetail, 
      envelopes: Envelope[], 
      rates: DaisyEurRates[], 
      returnDebugInfo = false
    ): DaisyEurRates | any | undefined => {
    
    const linkedEnvelopes = envelopes.filter(env => 
        env.user_id === detail.userId && 
        env.group_id === detail.groupId &&
        env.status !== 'voided'
    );

    const validRefIds = new Set(linkedEnvelopes.map(e => e.envelope_ref_id));

    if (linkedEnvelopes.length === 0 && !returnDebugInfo) {
        return undefined;
    }

    const matchedRate = rates.find(rate => {
        if (!rate.value) return false;
        if (!validRefIds.has(rate.value.refId)) return false;
        
        const rateEmail = normalize(rate.value.signer.email);
        const userEmail = normalize(detail.user.email);
        if (rateEmail !== userEmail) return false;

        const rateCat = (rate.value.yearCategory || "").trim().toLowerCase();
        const detailCat = (detail.year_category || "").trim().toLowerCase();
        return rateCat === detailCat;
    });

    if (returnDebugInfo) {
        return {
            linkedEnvelopesCount: linkedEnvelopes.length,
            linkedRefIds: Array.from(validRefIds),
            foundRate: matchedRate
        };
    }

    return matchedRate;

  }, []);

  // --- Contract Calculation Logic ---

  const recalculateContractDetails = useCallback((
    currentMap: Map<string, ContractDetail>,
    allEntities: Entity[],
    relevanceMap: Record<string, boolean>
  ) => {
    const detailsArray = Array.from(currentMap.entries()).map(([key, value]) => {
      const parts = key.split("-"); 
      const userId = parseInt(parts[0], 10);
      const groupId = parseInt(parts[1], 10);
      
      return { ...value, key, userId, groupId };
    });
  
    const grouped: Record<string, typeof detailsArray> = {};
    detailsArray.forEach((item) => {
      const groupKey = `${item.userId}-${item.groupId}`;
      if (!grouped[groupKey]) grouped[groupKey] = [];
      grouped[groupKey].push(item);
    });
  
    const updatedMap = new Map<string, ContractDetail>();
  
    Object.entries(grouped).forEach(([_, items]) => {
      const sortedItems = items.sort((a, b) => a.year_category_id - b.year_category_id);
      let carryOver = 0;
  
      sortedItems.forEach((item, index) => {
        let newWithdraw = 0;
  
        if (index === 0) {
          const payouts = allEntities
            .filter((e) => e.user_id === item.userId && e.group_id === item.groupId)
            .flatMap((e) => e.payouts);
  
          const seen = new Set<number>();
          payouts.forEach((p, i) => {
             const payoutKeySpecific = `${item.userId}-${item.groupId}-${item.year_category_id}-${i}`;
             const isRelevant = relevanceMap[payoutKeySpecific] ?? p.relevance;
            
            if (isRelevant && !seen.has(p.id)) {
              newWithdraw += p.valEur;
              seen.add(p.id);
            }
          });
        }
  
        const newDiff = index === 0 ? item.deposit - newWithdraw : item.deposit + carryOver;
        carryOver = newDiff < 0 ? newDiff : 0;
  
        updatedMap.set(item.key, {
          ...item,
          withdraw: newWithdraw,
          diff: newDiff
        });
      });
    });
  
    return updatedMap;
  }, []);

  // --- Data Loading ---

  const fetchData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      let intervalsData: any;
      let envelopesData: Envelope[];
      let ratesData: DaisyEurRates[];

      if (!forceRefresh) {
        const cachedIntervals = localStorage.getItem(CACHE_KEYS.INTERVALS);
        const cachedEnvelopes = localStorage.getItem(CACHE_KEYS.ENVELOPES);
        const cachedRates = localStorage.getItem(CACHE_KEYS.RATES);

        if (cachedIntervals && cachedEnvelopes && cachedRates) {
          intervalsData = JSON.parse(cachedIntervals);
          envelopesData = JSON.parse(cachedEnvelopes);
          ratesData = JSON.parse(cachedRates);
        }
      }

      // @ts-ignore
      if (!intervalsData || !envelopesData || !ratesData) {
        const start = options[0].startDate;
        const end = options[options.length - 1].endDate;

        const [res, envs, rates] = await Promise.all([
            getDaisyDepositIntervals(start, end),
            getEnvelopesWithGroup(),
            getDaisyEurRates()
        ]);

        intervalsData = res;
        envelopesData = envs;
        ratesData = rates || [];

        localStorage.setItem(CACHE_KEYS.INTERVALS, JSON.stringify(intervalsData));
        localStorage.setItem(CACHE_KEYS.ENVELOPES, JSON.stringify(envelopesData));
        localStorage.setItem(CACHE_KEYS.RATES, JSON.stringify(ratesData));
        
        if (forceRefresh) toast.success("Adatok frissítve a szerverről!");
      }

      setResult(intervalsData.entities);
      setEnvelopes(envelopesData);
      setEurRates(ratesData);

      const detailsMap = new Map<string, ContractDetail>();
      
      intervalsData.entities.forEach((e: Entity) => {
        const { id: catId, name: catName, paymentDeadline } = getYearCategory(e.date);
        if (catId === -1) return;

        const groupKey = `${e.user_id}-${e.group_id}-${catId}`;

        if (detailsMap.has(groupKey)) {
          const existing = detailsMap.get(groupKey)!;
          detailsMap.set(groupKey, {
            ...existing,
            deposit: existing.deposit + e.depositInEur,
          });
        } else {
          detailsMap.set(groupKey, {
            key: groupKey,
            user: e.user,
            userId: e.user_id,
            groupId: e.group_id,
            year_category: catName,
            year_category_id: catId,
            until_date: paymentDeadline,
            invested_date: e.date.toString(),
            deposit: e.depositInEur,
            withdraw: 0,
            diff: 0,
            remainingAmount: 0,
            currency: 'EUR',
          });
        }
      });

      const calculated = recalculateContractDetails(detailsMap, intervalsData.entities, {});
      setContractDetailsMap(calculated);

    } catch (error) {
      console.error(error);
      toast.error("Hiba az adatok betöltésekor.");
    } finally {
      setLoading(false);
    }
  }, [recalculateContractDetails]);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);


  // --- Toggle Logic ---

  async function togglePayoutStatus(payoutId: number): Promise<boolean> {
    try {
      await responseHero.get(`${process.env.REACT_APP_API_URL}/togglePayout/${payoutId}`);
      return true;
    } catch (error) {
      console.error("API error:", error);
      throw error;
    }
  }

  const handleTogglePayout = async (payoutId: number, mapKey: string) => {
    if (loadingPayouts[payoutId]) return;

    setLoadingPayouts((prev) => ({ ...prev, [payoutId]: true }));
    const prevMap = { ...payoutRelevanceMap };
    
    const currentVal = prevMap[mapKey] ?? true; 
    const newVal = !currentVal;

    const newMap = { ...prevMap, [mapKey]: newVal };
    setPayoutRelevanceMap(newMap);

    setContractDetailsMap((prev) => recalculateContractDetails(prev, result, newMap));

    try {
      await togglePayoutStatus(payoutId);
    } catch (error) {
        toast.error("Nem sikerült menteni a módosítást.");
        setPayoutRelevanceMap(prevMap);
        setContractDetailsMap((prev) => recalculateContractDetails(prev, result, prevMap));
    } finally {
        setLoadingPayouts((prev) => {
            const u = { ...prev };
            delete u[payoutId];
            return u;
        });
    }
  };

  // --- Manual Payout Logic ---

  const fetchManualPayoutsForUser = async (userId: number) => {
      setLoadingManualPayouts(prev => ({ ...prev, [userId]: true }));
      try {
          const data = await getDaisyPayoutRegister(userId);
          setManualPayouts(prev => ({ ...prev, [userId]: data }));
      } catch (error) {
          console.error("Failed to fetch manual payouts", error);
          toast.error("Nem sikerült betölteni a manuális kifizetéseket.");
      } finally {
          setLoadingManualPayouts(prev => ({ ...prev, [userId]: false }));
      }
  };

  // 1. DELETE Action
  const handleDeleteManualPayout = async (payout: DaisyPayoutRegister) => {
      if(!window.confirm(`Biztosan törlöd ezt a kifizetést? (${payout.amount} ${payout.currency})\nEz a művelet nem visszavonható.`)) return;

      try {
          await removeDaisyPayoutRegister(payout.id); // Call the API
          toast.success("Sikeres törlés!");
          
          // Refresh the user's list
          fetchManualPayoutsForUser(payout.user_id);
      } catch (error) {
          console.error(error);
          toast.error("Hiba történt a törlés során.");
      }
  };

  // 2. OPEN Modal
  const handleCreateNewPayout = (userId: number) => {
      setModalState({ open: true, userId });
  };

  // 3. CLOSE Modal
  const handleCloseModal = () => {
    setModalState({ open: false, userId: null });
  };

  // 4. REFRESH after Modal Success
  const handleModalSuccess = () => {
      if (modalState.userId) {
          fetchManualPayoutsForUser(modalState.userId);
      }
  };


  // --- Grouping & Filtering ---

  const groupedUsers = useMemo(() => {
    const usersMap = new Map<number, UserGroupedData>();
    const normalizedQuery = normalize(nameQuery.trim());

    contractDetailsMap.forEach((detail) => {
        if (normalizedQuery && !normalize(detail.user.name).includes(normalizedQuery)) {
            return;
        }

        if (!usersMap.has(detail.userId)) {
            usersMap.set(detail.userId, {
                user: detail.user,
                details: [],
                rawEntities: [],
                totalDeposit: 0,
                totalWithdraw: 0,
                totalDiff: 0
            });
        }

        const userData = usersMap.get(detail.userId)!;
        userData.details.push(detail);
        userData.totalDeposit += detail.deposit;
        userData.totalWithdraw += detail.withdraw;
        userData.totalDiff += detail.diff;
    });

    result.forEach(entity => {
         if (usersMap.has(entity.user_id)) {
             usersMap.get(entity.user_id)!.rawEntities.push(entity);
         }
    });

    return Array.from(usersMap.values()).sort((a, b) => 
        a.user.name.trim().localeCompare(b.user.name.trim(), 'hu', { sensitivity: 'base' })
    );

  }, [contractDetailsMap, result, nameQuery]);

  // --- Total Interest Calculation ---
  const { totalGlobalInterest, matchedContractsCount } = useMemo(() => {
    let interestSum = 0;
    let validContracts = 0;

    groupedUsers.forEach(user => {
        user.details.forEach(detail => {
            if (detail.diff >= 0) {
                // Pass envelopes here
                const match = findMatchingContract(detail, envelopes, eurRates) as DaisyEurRates | undefined;
                if (match && match.value) {
                    if (match.value.interestRate) {
                        interestSum += match.value.interestRate;
                    }
                    validContracts++; 
                }
            }
        });
    });
    return { totalGlobalInterest: interestSum, matchedContractsCount: validContracts };
  }, [groupedUsers, envelopes, eurRates, findMatchingContract]);

  
  // --- Actions ---

  const toggleUserExpand = (userId: number) => {
    const newSet = new Set(expandedUserIds);
    if (newSet.has(userId)) {
        newSet.delete(userId);
    } else {
        newSet.add(userId);
        // Fetch manual payouts if not already in state
        if (!manualPayouts[userId]) {
            fetchManualPayoutsForUser(userId);
        }
    }
    setExpandedUserIds(newSet);
  };

  const handleRefreshData = () => {
    fetchData(true); 
    setManualPayouts({});
  };

  // --- Render ---

  return (
    <div className="m-10">
      
      {/* Header / Search & Count */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 flex items-end gap-4">
        <div className='flex-1'>
            <label className="block text-sm font-medium text-gray-700 mb-1">Keresés név szerint</label>
            <input
            type="text"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            placeholder="Pl.: Kovács..."
            className="border px-3 py-2 rounded w-full border-gray-300 focus:ring-brown focus:border-brown"
            />
        </div>
        <div className="flex items-center gap-2 pb-1">
            <span className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                {groupedUsers.length} ügyfél
                <span className='ml-1 text-gray-500'>({matchedContractsCount} szerződéssel)</span>
            </span>

            <span className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-green-50 text-green-800 border border-green-200">
                Összesített kamat:{' '}<span className='font-semibold ml-1'>{currencyFormatter(totalGlobalInterest, 'EUR')}</span>
            </span>

            <button
                onClick={handleRefreshData}
                disabled={loading}
                className="bg-dark-blue hover:bg-blue-800 text-white px-4 py-2 rounded text-sm transition-colors disabled:opacity-50"
            >
                {loading ? 'Frissítés...' : 'Adatok frissítése'}
            </button>
        </div>
      </div>

      {loading ? (
        <p className='text-xl text-center animate-pulse text-gray-500'>Adatok betöltése...</p>
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-brown text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Felhasználó</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Kategóriák száma</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Össz. Befizetés (EUR)</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Össz. Kifizetés (EUR)</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Egyenleg (EUR)</th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Művelet</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {groupedUsers.length === 0 && (
                 <tr><td colSpan={6} className="p-4 text-center text-gray-500">Nincs találat.</td></tr>
              )}
              
              {groupedUsers.map((data) => {
                const isExpanded = expandedUserIds.has(data.user.user_id);
                return (
                  <Fragment key={data.user.user_id}>
                    {/* Main User Row */}
                    <tr 
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}
                        onClick={() => toggleUserExpand(data.user.user_id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">{data.user.name}</div>
                        <div className="text-xs text-gray-500">{data.user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {data.details.length} db befizetés
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {currencyFormatter(data.totalDeposit, 'EUR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                        {currencyFormatter(data.totalWithdraw, 'EUR')}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-bold ${data.totalDiff > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                        {currencyFormatter(data.totalDiff, 'EUR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <span className="text-brown hover:text-brown-dark select-none">
                          {isExpanded ? 'Bezárás ▲' : 'Megnyitás ▼'}
                        </span>
                      </td>
                    </tr>

                    {/* Expanded Detail Row */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={6} className="px-4 py-4 sm:px-6 border-b border-gray-200">
                            
                            {/* 1. Contract Intervals List */}
                            <h4 className="text-sm font-bold text-dark-blue mb-2 uppercase">Befizetések</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                                {data.details.sort((a,b) => a.year_category_id - b.year_category_id).map((detail) => {
                                    
                                    const matchingContract = findMatchingContract(detail, envelopes, eurRates) as DaisyEurRates | undefined;
                                    const debugInfo = debugMode === detail.key ? findMatchingContract(detail, envelopes, eurRates, true) : null;

                                    return (
                                        <div key={detail.key} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col justify-between">
                                            <div>
                                                <div className="flex justify-between items-start mb-2 border-b border-gray-100 pb-2">
                                                    <span className="bg-brown text-white text-xs px-2 py-1 rounded font-semibold">
                                                        {detail.year_category}
                                                    </span>
                                                    <span className="text-xs text-gray-400 font-mono">ID: {detail.groupId}</span>
                                                </div>

                                                <div className="space-y-1 text-sm">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">Befizetés dátuma:</span>
                                                        <span className="text-gray-400">
                                                          {new Date(detail.invested_date).toLocaleDateString('hu-HU')}
                                                        </span>
                                                    </div>

                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">Befizetés:</span>
                                                        <div className="text-right leading-tight">
                                                          <span className="font-medium">{currencyFormatter(detail.deposit, 'EUR')}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">Kifizetés:</span>
                                                        <span className="text-red-500">{currencyFormatter(detail.withdraw, 'EUR')}</span>
                                                    </div>

                                                    <div className="flex justify-between border-t border-dashed border-gray-300 pt-2 mt-2">
                                                        <span className="font-bold text-gray-800">Különbözet:</span>
                                                        <span className={`font-bold ${detail.diff > 0 ? 'text-green-600' : 'text-gray-800'}`}>
                                                            {currencyFormatter(detail.diff, 'EUR')}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Contract Display Area */}
                                                    {detail.diff >= 0 && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                                            {matchingContract && matchingContract.value ? (
                                                                <div className="bg-green-50 p-2 rounded text-xs space-y-1">
                                                                    <div className="flex justify-between">
                                                                        <span className="text-gray-600">Kamat:</span>
                                                                        <span className="font-semibold text-green-700">
                                                                            {Math.round(matchingContract.value.interestRate)} EUR
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-gray-600">Választott opció:</span>
                                                                        <span className="font-semibold text-gray-800">
                                                                            {getRadioValue(matchingContract.value.radioValue)}
                                                                    </span>
                                                                    </div>
                                                                    <div className="flex justify-between border-t border-green-200 pt-1 mt-1 text-green-900">
                                                                        <span className="text-[10px]">Szerződés összege:</span>
                                                                        <span className="text-[10px] font-mono">{matchingContract.value.diff} EUR</span>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="bg-red-50 p-2 rounded text-xs">
                                                                    <div className="text-center text-red-600 font-medium flex justify-between items-center">
                                                                        <span>Nincs kitöltött szerződés.</span>
                                                                        <button 
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setDebugMode(debugMode === detail.key ? null : detail.key);
                                                                            }}
                                                                            title="Hibakeresés"
                                                                            className="text-xs bg-red-100 hover:bg-red-200 px-1 rounded ml-2"
                                                                        >
                                                                            🕵️
                                                                        </button>
                                                                    </div>
                                                                    {debugMode === detail.key && debugInfo && (
                                                                        <div className="mt-2 border-t border-red-200 pt-2">
                                                                            <p className="font-bold text-[10px] text-gray-600 mb-1">
                                                                                Linkelt Envelope ID-k: {debugInfo.linkedEnvelopesCount} db
                                                                            </p>
                                                                            <p className="text-[10px] text-gray-500 mb-1 break-all">
                                                                                Ref ID-k: {debugInfo.linkedRefIds.join(', ')}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* 2. Raw Payouts Audit */}
                            {data.rawEntities.length > 0 && (
                                <details className="group mb-6">
                                    <summary className="cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-800 mb-2 select-none">
                                        ▶ Részletes automatikus tranzakciók (Audit)
                                    </summary>
                                    <div className="overflow-x-auto border rounded-md mt-2 bg-white">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-gray-100 text-gray-600">
                                                <tr>
                                                    <th className="py-2 px-3 text-left">Számításba vesz</th>
                                                    <th className="py-2 px-3 text-left">Dátum</th>
                                                    <th className="py-2 px-3 text-left">Típus</th>
                                                    <th className="py-2 px-3 text-right">Összeg (USD)</th>
                                                    <th className="py-2 px-3 text-right">Összeg (EUR)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {data.rawEntities.flatMap((ent) => 
                                                    ent.payouts.map((p, idx) => {
                                                        const catId = getYearCategory(ent.date).id;
                                                        const mapKey = `${ent.user_id}-${ent.group_id}-${catId}-${idx}`;
                                                        const isChecked = payoutRelevanceMap[mapKey] ?? p.relevance;

                                                        return (
                                                            <tr key={`${ent.id}-${p.id}`} className="hover:bg-gray-50">
                                                                <td className="py-2 px-3">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={isChecked}
                                                                        disabled={loadingPayouts[p.id]}
                                                                        onChange={() => handleTogglePayout(p.id, mapKey)}
                                                                        className="rounded text-brown focus:ring-brown"
                                                                    />
                                                                </td>
                                                                <td className="py-2 px-3">{new Date(p.date).toLocaleDateString()}</td>
                                                                <td className="py-2 px-3">{p.payout}</td>
                                                                <td className="py-2 px-3 text-right">{currencyFormatter(p.val, 'USD')}</td>
                                                                <td className="py-2 px-3 text-right">{currencyFormatter(p.valEur, 'EUR')}</td>
                                                            </tr>
                                                        )
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </details>
                            )}

                            {/* 3. Manual Payouts Register */}
                            <div className="mt-6 pt-4 border-t border-gray-300">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-sm font-bold text-dark-blue uppercase flex items-center gap-2">
                                        Kifizetések
                                        {loadingManualPayouts[data.user.user_id] && 
                                            <span className="text-xs font-normal text-gray-500 animate-pulse">(Betöltés...)</span>
                                        }
                                    </h4>
                                    <button 
                                        onClick={() => handleCreateNewPayout(data.user.user_id)}
                                        className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-2 rounded shadow-sm transition-colors"
                                    >
                                        + Új kifizetés rögzítése
                                    </button>
                                </div>

                                <div className="overflow-x-auto border rounded-md bg-white shadow-sm">
                                    <table className="min-w-full text-sm divide-y divide-gray-200">
                                        <thead className="bg-gray-100 text-gray-700 font-medium">
                                            <tr>
                                                <th className="py-2 px-3 text-left">Dátum</th>
                                                <th className="py-2 px-3 text-left">Hálózat</th>
                                                <th className="py-2 px-3 text-left">Deviza</th>
                                                <th className="py-2 px-3 text-left">Tárca címe</th>
                                                <th className="py-2 px-3 text-left">Hash</th>
                                                <th className="py-2 px-3 text-right">Összeg</th>
                                                <th className="py-2 px-3 text-center">Műveletek</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {!manualPayouts[data.user.user_id] || manualPayouts[data.user.user_id].length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="py-4 text-center text-gray-500 text-xs italic">
                                                        Nincs rögzített kifizetés.
                                                    </td>
                                                </tr>
                                            ) : (
                                                manualPayouts[data.user.user_id].map((payout) => (
                                                    <tr key={payout.id} className="hover:bg-gray-50">
                                                        <td className="py-2 px-3 text-gray-900">
                                                            {new Date(payout.date).toLocaleDateString('hu-HU')}
                                                        </td>
                                                        <td className="py-2 px-3 text-gray-600">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                {payout.network}
                                                            </span>
                                                        </td>
                                                        
                                                        
                                                        <td className="py-2 px-3 text-gray-600 font-medium">{payout.currency}</td>
                                                        
                                                        <td className="py-2 px-3 text-gray-500 text-xs font-mono max-w-xs truncate" title={payout.hash}>
                                                            {payout.wallet}
                                                        </td>
                                                        
                                                        <td className="py-2 px-3 text-gray-500 text-xs font-mono max-w-xs truncate" title={payout.hash}>
                                                            {payout.hash}
                                                        </td>
                                                        <td className="py-2 px-3 text-right font-bold text-gray-900">
                                                            {payout.amount}
                                                        </td>
                                                        <td className="py-2 px-3 text-center">
                                                            <button 
                                                                onClick={() => handleDeleteManualPayout(payout)}
                                                                className="text-red-500 hover:text-red-700 text-xs font-medium underline"
                                                            >
                                                                Törlés
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual Payout Modal */}
      {modalState.userId && (
        <DaisyPayoutModal 
            isOpen={modalState.open}
            userId={modalState.userId}
            onClose={handleCloseModal}
            onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
}